import * as React from 'react'
import { useHistory, useLocation } from 'react-router'

import { SmsVerificationModal } from './SmsVerificationModal'

import {
  formatTel,
  getRawPhoneNumberWithCountryCode
} from '@local/do-secundo-form-utils'
import {
  handleVerificationCodeSubmit,
  useTrackCodeSubmissionSuccess,
  useSendSmsCode,
  useImportAccount,
  useIsAccountLinkingEnabled,
  isLegacyAccountLoggedInForAccountLinking
} from '@local/do-secundo-passwordless-authentication'
import {
  useAuth,
  importSuccessToast
} from '../../../../client/components/AuthProvider/AuthProvider'
import { ModalHeaderWithBackButton } from '../ModalHeaderWithBackButton'
import { ModeRedirect } from '../../../../client/components/ModeRouter/utils'

export interface PasswordlessCodeVerificationModalProps {
  onClose: () => void
}

/**
 * @deprecated will be replaced by UnifiedSmsVerificationModal for v1.5 passwordless
 */
export const PasswordlessCodeVerificationModal = ({
  onClose
}: PasswordlessCodeVerificationModalProps) => {
  const trackCodeSubmissionSuccess = useTrackCodeSubmissionSuccess()
  const { authClient, setIsAuthenticated, passwordlessLogin } = useAuth()
  const handleImportAccount = useImportAccount()
  const history = useHistory()
  const ACCOUNT_LINKING_ENABLED = useIsAccountLinkingEnabled()
  const { state, pathname } = useLocation<{
    from?: string
    guestPhone?: string
  }>()
  const from = state?.from
  const dataIdSuffix = state?.from === 'login' ? 'login' : 'create'
  const phone = state?.guestPhone
  const mainText =
    state?.from === 'login' ? (
      <p className='mb-4 type-default' data-testid='sms-modal-description'>
        You're one step closer to logging in. Enter the code we sent to&nbsp;
        <b>{formatTel(phone)}</b> to finish logging in.
      </p>
    ) : (
      <p className='mb-4 type-default' data-testid='sms-modal-description'>
        You're one step closer to creating your account. Enter the code we sent
        to&nbsp;<b>{formatTel(phone)}</b> to finish creating your account.
      </p>
    )

  useSendSmsCode(phone, authClient)

  if (!phone) {
    return <ModeRedirect pathname={pathname} mode='login' />
  }

  return (
    <SmsVerificationModal
      onClose={onClose}
      handleSubmit={async (code: string) => {
        await handleVerificationCodeSubmit({
          code,
          onCodeSubmissionSuccess: onClose,
          trackCodeSubmissionSuccess: () => trackCodeSubmissionSuccess(from),
          completeProfileCreation: passwordlessLogin,
          from
        })

        if (
          ACCOUNT_LINKING_ENABLED &&
          isLegacyAccountLoggedInForAccountLinking()
        ) {
          await handleImportAccount({
            onSuccess: () => {
              importSuccessToast()
              onClose()
            }
          })
        }
        setIsAuthenticated(authClient?.isAuthenticated)
      }}
      numFields={6}
      phone={formatTel(phone!)}
      header={
        <ModalHeaderWithBackButton
          onClick={() => history.goBack()}
          headerText='One step away'
          testId={`sms-verification-modal-${dataIdSuffix}-back-button`}
        />
      }
      mainText={mainText}
      handleResend={() =>
        authClient?.requestPasswordless(getRawPhoneNumberWithCountryCode(phone))
      }
    />
  )
}
